/**
 * "Start from" -- the template gallery (ADR-0013, ADR-0016, ADR-0017).
 *
 * Two shelves: what ships with the build, and what this workspace has saved. The
 * subtitle says what both are -- *starting points, not verified calculations* --
 * because a gallery is exactly where a reader would otherwise assume that something we
 * ship carries our assurance (1.3, ADR-0013 rule 4). Nothing here wears a badge, and
 * curated content that computes something is not visually distinguished from structure
 * that does not: a shelf that ranked its own entries would be the middle tier 1.3
 * forbids.
 *
 * Built-in entries are grouped under their category heading; the workspace shelf is
 * grouped under whatever tags the firm typed, because that taxonomy is theirs and not
 * ours to translate. Search spans both.
 *
 * Picking one hands the caller a materialised `SheetDoc`. Creating the sheet is the
 * caller's ordinary create call; this component never talks to `/sheets`.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Archive, ArchiveRestore, FileText, LayoutTemplate, Search, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useAuth } from '@/auth/AuthProvider'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import type { SheetDoc } from '../model/types'
import {
  deleteWorkspaceTemplate,
  getWorkspaceTemplate,
  listTemplates,
  materialiseStarter,
  materialiseWorkspaceTemplate,
  TEMPLATES_QUERY_KEY,
  updateWorkspaceTemplate,
  type StarterRecipe,
  type WorkspaceTemplateSummary,
} from '../templates'

/** Case- and accent-insensitive folding, so "generale" finds "Générale".
 *  The range is written as escapes rather than literal combining marks, which are
 *  invisible in source and do not survive every editor. */
function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
}

export function TemplateGallery({
  open,
  onOpenChange,
  onPick,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onPick: (document: SheetDoc) => void
}) {
  const { t } = useTranslation(['sheets', 'common'])
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [busyId, setBusyId] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [activeCategory, setActiveCategory] = useState<string | null>(null)

  const catalogue = useQuery({
    queryKey: TEMPLATES_QUERY_KEY,
    queryFn: ({ signal }) => listTemplates(signal),
    enabled: open,
  })

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: TEMPLATES_QUERY_KEY })

  const pickWorkspace = useMutation({
    mutationFn: async (id: number) => materialiseWorkspaceTemplate(await getWorkspaceTemplate(id)),
    onSuccess: (document) => onPick(document),
    onSettled: () => setBusyId(null),
  })

  const shelve = useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      updateWorkspaceTemplate(id, { enabled }),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: number) => deleteWorkspaceTemplate(id),
    onSuccess: invalidate,
  })

  const pickStarter = (starter: StarterRecipe) => {
    // The recipe's prose keys resolve through the same catalogue as the UI, in the
    // reader's locale -- so the sheet a French engineer starts from is in French.
    onPick(materialiseStarter(starter, (key) => t(key, { ns: 'sheets' })))
  }

  const starters = catalogue.data?.starters ?? []
  const workspace = catalogue.data?.workspace ?? []
  const canCreate = catalogue.data?.capabilities.can_create ?? false
  const canCurate = catalogue.data?.capabilities.can_curate ?? false

  const categoryLabel = (key: string) =>
    t(`sheets:sheets.templates.category.${key}`, { defaultValue: key })

  /** Built-in categories in the server's order, then whatever tags the firm typed.
   *  Not memoised by hand: the React Compiler does it, and a manual `useMemo` here
   *  defeats it rather than helping. */
  const filters = [
    ...(catalogue.data?.categories ?? [])
      .filter((key) => starters.some((starter) => starter.category === key))
      .map((key) => ({ key, label: categoryLabel(key) })),
    ...[...new Set(workspace.map((row) => row.category).filter(Boolean))]
      .sort()
      .map((tag) => ({ key: `w:${tag as string}`, label: tag as string })),
  ]

  const needle = fold(query.trim())
  const matchesStarter = (starter: StarterRecipe) =>
    !needle ||
    fold(t(starter.title_key, { ns: 'sheets' })).includes(needle) ||
    fold(t(starter.description_key, { ns: 'sheets' })).includes(needle)
  const matchesWorkspace = (row: WorkspaceTemplateSummary) =>
    !needle ||
    fold(row.title).includes(needle) ||
    fold(row.description).includes(needle) ||
    fold(row.category ?? '').includes(needle)

  const shownStarters = starters.filter(
    (s) => matchesStarter(s) && (activeCategory === null || activeCategory === s.category),
  )
  const shownWorkspace = workspace.filter(
    (row) =>
      matchesWorkspace(row) &&
      (activeCategory === null || activeCategory === `w:${row.category ?? ''}`),
  )

  /** Group in the server's order so the shelf is arranged by data, not by chance. */
  const starterGroups = (catalogue.data?.categories ?? [])
    .map((key) => ({ key, items: shownStarters.filter((s) => s.category === key) }))
    .filter((group) => group.items.length > 0)

  const workspaceGroups = [...new Set(shownWorkspace.map((row) => row.category ?? ''))]
    .sort()
    .map((tag) => ({
      tag,
      items: shownWorkspace.filter((row) => (row.category ?? '') === tag),
    }))

  const nothingMatches = needle !== '' && shownStarters.length === 0 && shownWorkspace.length === 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85dvh] max-w-2xl flex-col">
        <DialogHeader>
          <DialogTitle>{t('sheets:sheets.templates.title')}</DialogTitle>
        </DialogHeader>

        <p className="text-sm leading-relaxed text-text-muted">
          {t('sheets:sheets.templates.subtitle')}
        </p>

        <div className="mt-3 flex items-center gap-2 rounded-md border border-border bg-surface px-3">
          <Search className="h-4 w-4 shrink-0 text-text-muted" aria-hidden />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('sheets:sheets.templates.search')}
            aria-label={t('sheets:sheets.templates.search')}
            className="min-h-[44px] w-full border-0 bg-transparent text-sm text-text placeholder:text-text-muted focus:outline-none"
          />
        </div>

        {filters.length > 1 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            <FilterChip
              label={t('sheets:sheets.templates.filter_all')}
              active={activeCategory === null}
              onClick={() => setActiveCategory(null)}
            />
            {filters.map((filter) => (
              <FilterChip
                key={filter.key}
                label={filter.label}
                active={activeCategory === filter.key}
                onClick={() =>
                  setActiveCategory((current) => (current === filter.key ? null : filter.key))
                }
              />
            ))}
          </div>
        )}

        <div className="-mx-1 mt-4 flex-1 overflow-y-auto px-1">
          {nothingMatches && (
            <p className="rounded-md border border-dashed border-border p-4 text-sm text-text-muted">
              {t('sheets:sheets.templates.no_matches', { query: query.trim() })}
            </p>
          )}

          {starterGroups.length > 0 && (
            <section aria-labelledby="starters-heading">
              <h3
                id="starters-heading"
                className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-muted"
              >
                {t('sheets:sheets.templates.starters')}
              </h3>
              {starterGroups.map((group) => (
                <div key={group.key} className="mb-4">
                  <h4 className="mb-1.5 text-[11px] font-medium text-text-muted">
                    {categoryLabel(group.key)}
                  </h4>
                  <ul className="grid gap-2 sm:grid-cols-2">
                    {group.items.map((starter) => (
                      <li key={starter.id}>
                        <button
                          type="button"
                          onClick={() => pickStarter(starter)}
                          className="flex min-h-[44px] w-full items-start gap-3 rounded-md border border-border bg-surface p-3 text-start hover:border-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                          data-testid={`starter-${starter.id}`}
                        >
                          <LayoutTemplate
                            className="mt-0.5 h-4 w-4 shrink-0 text-text-muted"
                            aria-hidden
                          />
                          <span className="min-w-0">
                            <span className="block text-sm font-medium">
                              {t(starter.title_key, { ns: 'sheets' })}
                            </span>
                            <span className="mt-0.5 block text-xs leading-relaxed text-text-muted">
                              {t(starter.description_key, { ns: 'sheets' })}
                            </span>
                            <span className="mt-1 block text-[11px] text-text-muted">
                              {t('sheets:sheets.templates.count.cells', {
                                count: starter.cell_count,
                              })}
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </section>
          )}

          {/* Hidden while a search matches nothing, so the reader gets one message
              rather than "nothing matches" stacked on top of an empty shelf. */}
          {!nothingMatches && (
            <section className="mt-2" aria-labelledby="workspace-heading">
              <h3
                id="workspace-heading"
                className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-muted"
              >
                {t('sheets:sheets.templates.workspace')}
              </h3>
              {workspace.length === 0 ? (
                <p className="rounded-md border border-dashed border-border p-3 text-xs leading-relaxed text-text-muted">
                  {/* A reader who cannot act is told what the shelf is, not what to
                    press -- the old string instructed everyone to use a control not
                    everyone has (ADR-0017 §4). */}
                  {canCreate
                    ? t('sheets:sheets.templates.workspace_empty')
                    : t('sheets:sheets.templates.workspace_empty_reader')}
                </p>
              ) : (
                workspaceGroups.map((group) => (
                  <div key={group.tag || '__untagged'} className="mb-4">
                    {workspaceGroups.length > 1 && (
                      <h4 className="mb-1.5 text-[11px] font-medium text-text-muted">
                        {group.tag || t('sheets:sheets.templates.untagged')}
                      </h4>
                    )}
                    <ul className="divide-y divide-border rounded-md border border-border">
                      {group.items.map((template) => (
                        <WorkspaceRow
                          key={template.id}
                          template={template}
                          busy={busyId === `w${template.id}`}
                          canCurate={canCurate}
                          isAuthor={user?.id === template.created_by}
                          onUse={() => {
                            setBusyId(`w${template.id}`)
                            pickWorkspace.mutate(template.id)
                          }}
                          onShelve={(enabled) => shelve.mutate({ id: template.id, enabled })}
                          onDelete={() => {
                            if (window.confirm(t('sheets:sheets.templates.delete_confirm'))) {
                              remove.mutate(template.id)
                            }
                          }}
                        />
                      ))}
                    </ul>
                  </div>
                ))
              )}
            </section>
          )}
        </div>

        <div className="mt-4 flex justify-end">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('sheets:sheets.templates.cancel')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`min-h-[32px] rounded-full border px-3 text-xs transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
        active
          ? 'border-accent bg-accent text-accent-fg'
          : 'border-border bg-surface text-text-muted hover:text-text'
      }`}
    >
      {label}
    </button>
  )
}

function WorkspaceRow({
  template,
  busy,
  canCurate,
  isAuthor,
  onUse,
  onShelve,
  onDelete,
}: {
  template: WorkspaceTemplateSummary
  busy: boolean
  canCurate: boolean
  /** A member may withdraw their own contribution, but not rename or shelve it. */
  isAuthor: boolean
  onUse: () => void
  onShelve: (enabled: boolean) => void
  onDelete: () => void
}) {
  const { t } = useTranslation('sheets')

  return (
    <li className="flex items-center gap-3 p-3">
      <FileText className="h-4 w-4 shrink-0 text-text-muted" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-medium">{template.title}</span>
          {!template.enabled && (
            <span className="rounded border border-border px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-text-muted">
              {t('sheets.templates.shelved')}
            </span>
          )}
        </div>
        {template.description && (
          <p className="mt-0.5 truncate text-xs text-text-muted">{template.description}</p>
        )}
        <p className="mt-0.5 text-[11px] text-text-muted">
          {t('sheets.templates.count.cells', { count: template.cell_count })}
          {template.created_by_name && (
            <>
              {' · '}
              {t('sheets.templates.saved_by', { name: template.created_by_name })}
            </>
          )}
        </p>
      </div>

      {canCurate && (
        <button
          type="button"
          onClick={() => onShelve(!template.enabled)}
          aria-label={
            template.enabled ? t('sheets.templates.shelve') : t('sheets.templates.unshelve')
          }
          title={template.enabled ? t('sheets.templates.shelve') : t('sheets.templates.unshelve')}
          className="flex h-11 w-11 items-center justify-center text-text-muted hover:text-text"
        >
          {template.enabled ? (
            <Archive className="h-4 w-4" aria-hidden />
          ) : (
            <ArchiveRestore className="h-4 w-4" aria-hidden />
          )}
        </button>
      )}

      {(canCurate || isAuthor) && (
        <button
          type="button"
          onClick={onDelete}
          aria-label={t('sheets.templates.delete')}
          title={t('sheets.templates.delete')}
          className="flex h-11 w-11 items-center justify-center text-text-muted hover:text-[var(--status-fail)]"
        >
          <Trash2 className="h-4 w-4" aria-hidden />
        </button>
      )}

      <Button size="sm" onClick={onUse} loading={busy} disabled={!template.enabled}>
        {t('sheets.templates.use')}
      </Button>
    </li>
  )
}
